<script setup lang="ts">
import ParticipantMeta from 'src/shared/ui/participant-meta/participant-meta.vue';
import type { ParticipantType } from 'src/shared/ui/participant-meta/participant-meta.vue';
import avatarPlaceholder from 'src/shared/assets/avatar-placeholder.png';
import { Typography } from 'src/shared/ui/typography';

export type { ParticipantType } from 'src/shared/ui/participant-meta/participant-meta.vue';

const props = withDefaults(
  defineProps<{
    name: string;
    photo?: string | null;
    userType: ParticipantType;
    reviewsCount: number;
    rating: number;
    compact?: boolean;
  }>(),
  {
    photo: null,
    compact: false,
  },
);
</script>

<template>
  <div :class="['participant-summary', { 'participant-summary--compact': compact }]">
    <img class="participant-summary__photo" :src="props.photo || avatarPlaceholder" :alt="name" />
    <div class="participant-summary__content">
      <slot name="name">
        <Typography variant="body-1" as="p" weight="700" class="participant-summary__name">
          {{ name }}
        </Typography>
      </slot>
      <ParticipantMeta
        :user-type="userType"
        :reviews-count="reviewsCount"
        :rating="rating"
        :compact="compact"
      />
    </div>
  </div>
</template>

<style scoped>
.participant-summary {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-l);
  padding-top: var(--participant-summary-padding-top, 0);
  text-align: center;
}

.participant-summary__photo {
  width: var(--participant-summary-photo-size, 40px);
  height: var(--participant-summary-photo-size, 40px);
  flex: 0 0 var(--participant-summary-photo-size, 40px);
  border: 1px solid var(--palette-light-graphite);
  border-radius: 50%;
  object-fit: cover;
}

.participant-summary__content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-s);
}

.participant-summary__name {
  max-width: 100%;
  margin: 0;
  overflow-wrap: anywhere;
}

.participant-summary--compact {
  flex-direction: row;
  align-items: flex-start;
  gap: var(--spacing-xl);
  text-align: left;
}

.participant-summary :deep(.participant-summary__meta) {
  justify-content: center;
}

.participant-summary--compact :deep(.participant-summary__meta) {
  justify-content: flex-start;
}

.participant-summary--compact .participant-summary__photo {
  width: 80px;
  height: 80px;
  flex-basis: 80px;
}

.participant-summary--compact .participant-summary__content {
  align-items: flex-start;
}

@media (max-width: 755px) {
  .participant-summary--compact .participant-summary__photo {
    width: 64px;
    height: 64px;
    flex-basis: 64px;
  }
}
</style>
